import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { api } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  FileText, Download, BookOpen, ScrollText, ClipboardList, Info,
  ShieldAlert, ArrowLeft, Loader2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const docs = [
  {
    id: 'user_guide',
    filename: 'EnglishVocaLab_User_Guide.pdf',
    title: 'Complete User Guide',
    subtitle: 'Administrator, Teacher & Student Reference Manual',
    description: 'Covers all features for Admins, Teachers, and Students — with step-by-step instructions, keyboard shortcuts, lock/delete system, test modules, and more.',
    icon: BookOpen,
    color: 'indigo',
    badge: 'PDF',
  },
  {
    id: 'test_creation',
    filename: 'EnglishVocaLab_TestCreationGuide.pdf',
    title: 'Test Creation Guide',
    subtitle: 'All 4 Test Modules: TTS · STT · MCQ · Cinematic MCQ',
    description: 'Step-by-step guide for creating tests in all four modules. Covers question formats, Excel import, class assignment, locking, editing, and best practices for each module type.',
    icon: ClipboardList,
    color: 'violet',
    badge: 'PDF',
  },
  {
    id: 'terms',
    filename: 'EnglishVocaLab_TermsAndConditions.pdf',
    title: 'Terms & Conditions',
    subtitle: 'End User License Agreement (EULA)',
    description: 'Legal terms governing the use of English VocaLab, including license grant, restrictions, data privacy, content ownership, and liability limitations.',
    icon: ScrollText,
    color: 'slate',
    badge: 'PDF',
  },
];

const colorMap = {
  indigo: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    icon: 'text-indigo-600 dark:text-indigo-400',
    subtitle: 'text-indigo-600 dark:text-indigo-400',
    btn: 'bg-indigo-600 hover:bg-indigo-700',
    border: 'border-indigo-200 dark:border-indigo-700',
    hover: 'hover:border-indigo-400 dark:hover:border-indigo-500',
  },
  violet: {
    bg: 'bg-violet-100 dark:bg-violet-900/30',
    icon: 'text-violet-600 dark:text-violet-400',
    subtitle: 'text-violet-600 dark:text-violet-400',
    btn: 'bg-violet-600 hover:bg-violet-700',
    border: 'border-violet-200 dark:border-violet-700',
    hover: 'hover:border-violet-400 dark:hover:border-violet-500',
  },
  amber: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    icon: 'text-amber-600 dark:text-amber-400',
    subtitle: 'text-amber-600 dark:text-amber-400',
    btn: 'bg-amber-600 hover:bg-amber-700',
    border: 'border-amber-200 dark:border-amber-700',
    hover: 'hover:border-amber-400 dark:hover:border-amber-500',
  },
  slate: {
    bg: 'bg-slate-100 dark:bg-slate-800/60',
    icon: 'text-slate-600 dark:text-slate-400',
    subtitle: 'text-slate-600 dark:text-slate-400',
    btn: 'bg-slate-600 hover:bg-slate-700',
    border: 'border-slate-200 dark:border-slate-700',
    hover: 'hover:border-slate-400 dark:hover:border-slate-500',
  },
};

const ResourcesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [iframeReady, setIframeReady] = useState(false);

  // Restrict to admin and teacher only
  if (user && user.role === 'student') {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <ShieldAlert className="h-12 w-12 text-amber-500" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Access Restricted</h2>
          <p className="text-slate-500 dark:text-slate-400 text-center max-w-md">
            This page is available for Administrators and Teachers only.
          </p>
          <Button onClick={() => navigate(-1)} variant="outline">Go Back</Button>
        </div>
      </DashboardLayout>
    );
  }

  const getViewUrl = (filename) => {
    const token = localStorage.getItem('vocalab_token') || '';
    // Append Chrome PDF viewer hash params to hide toolbar, sidebar, and scrollbar controls
    return `${BACKEND_URL}/api/docs/view/${encodeURIComponent(filename)}?token=${encodeURIComponent(token)}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
  };

  const openDoc = (doc) => {
    setIframeReady(false);
    setSelectedDoc(doc);
  };

  const handleDownload = async () => {
    if (!selectedDoc) return;
    try {
      const res = await api.get(`/docs/download/${selectedDoc.filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedDoc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${selectedDoc.filename} downloaded`);
    } catch {
      toast.error('Download failed. Please try again.');
    }
  };

  const goBack = () => {
    setSelectedDoc(null);
    setIframeReady(false);
  };

  // ── PDF VIEWER VIEW ──
  if (selectedDoc) {
    const colors = colorMap[selectedDoc.color];
    const Icon = selectedDoc.icon;
    const pdfUrl = getViewUrl(selectedDoc.filename);

    return (
      <DashboardLayout>
        <div className="flex flex-col space-y-4 animate-fade-in">
          {/* Header bar */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={goBack}
                className="flex items-center gap-2 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Documents
              </Button>
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-lg ${colors.bg}`}>
                  <Icon className={`h-5 w-5 ${colors.icon}`} />
                </div>
                <div>
                  <h2 className="font-bold text-slate-900 dark:text-white text-base leading-tight">
                    {selectedDoc.title}
                  </h2>
                  <p className={`text-xs ${colors.subtitle}`}>{selectedDoc.subtitle}</p>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleDownload}
              className="flex items-center gap-2 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
          </div>

          {/* PDF Viewer */}
          <div
            className="rounded-xl overflow-hidden border border-slate-200 dark:border-white/10 bg-slate-100 dark:bg-slate-900 relative"
            style={{ height: '78vh' }}
          >
            {/* Loading overlay — hidden once iframe fires onLoad */}
            {!iframeReady && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10 bg-slate-100 dark:bg-slate-900">
                <Loader2 className="h-10 w-10 animate-spin text-indigo-500" />
                <p className="text-slate-500 dark:text-slate-400 text-sm">Loading document…</p>
              </div>
            )}
            <embed
              key={selectedDoc.filename}
              src={pdfUrl}
              type="application/pdf"
              className="w-full h-full"
              style={{ border: 'none', display: 'block' }}
              onLoad={() => setIframeReady(true)}
            />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── DOCS LIST VIEW ──
  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileText className="h-7 w-7 text-indigo-600" />
            Resources &amp; Documentation
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Browse and read guides and documentation for English VocaLab
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {docs.map(doc => {
            const Icon = doc.icon;
            const colors = colorMap[doc.color];
            return (
              <Card
                key={doc.id}
                className={`hover:shadow-xl transition-all group cursor-pointer border-2 ${colors.border} ${colors.hover}`}
                onClick={() => openDoc(doc)}
                data-testid={`doc-card-${doc.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`p-3 rounded-xl ${colors.bg} group-hover:scale-110 transition-transform`}>
                      <Icon className={`h-7 w-7 ${colors.icon}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-slate-900 dark:text-white">{doc.title}</h3>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-mono">
                          {doc.badge}
                        </span>
                      </div>
                      <p className={`text-sm ${colors.subtitle} font-medium`}>{doc.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mb-5 leading-relaxed">
                    {doc.description}
                  </p>
                  <div className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-white text-sm font-medium ${colors.btn} transition-colors`}>
                    <ExternalLink className="h-4 w-4" />
                    Open Document
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2">Quick Reference</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-600 dark:text-slate-300">
                  <div>
                    <p className="font-medium text-slate-800 dark:text-slate-200 mb-1">Video Player Shortcuts:</p>
                    <ul className="space-y-0.5">
                      <li>• <kbd className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-xs">Space</kbd> — Play/Pause</li>
                      <li>• <kbd className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-xs">←→</kbd> — Seek ±10 seconds</li>
                      <li>• <kbd className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-xs">↑↓</kbd> — Volume</li>
                      <li>• <kbd className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-xs">F</kbd> — Fullscreen</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 dark:text-slate-200 mb-1">Image Viewer Shortcuts:</p>
                    <ul className="space-y-0.5">
                      <li>• <kbd className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-xs">Space</kbd> — Pause/Resume timer</li>
                      <li>• <kbd className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-xs">ESC</kbd> — Close (after timer)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ResourcesPage;
